import { homedir } from "node:os";
import { join } from "node:path";
import { chmod, mkdir, readFile, rm, writeFile } from "node:fs/promises";

/**
 * On-disk store for `carrier login` credentials.
 *
 * Deliberately a 0600 file rather than the macOS Keychain. The Keychain write
 * path (`security add-generic-password -w <secret>`) puts the secret in argv,
 * where any process running `ps` can read it — worse than a mode-0600 file in a
 * mode-0700 directory. This is the same choice gh, aws and gcloud make.
 */

export interface StoredCredentials {
  /** OAuth issuer these tokens belong to; a mismatch means the store is stale. */
  issuer: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch ms when the access token expires. */
  expiresAt: number;
  scope?: string;
  /** Dynamic-registration client id, reused across logins on this machine. */
  clientId?: string;
}

export function carrierHome(): string {
  return process.env.CARRIER_HOME?.trim() || join(homedir(), ".carrier");
}

function credentialsPath(): string {
  return join(carrierHome(), "credentials.json");
}

export async function readCredentials(): Promise<StoredCredentials | null> {
  try {
    const raw = await readFile(credentialsPath(), "utf8");
    const parsed = JSON.parse(raw) as StoredCredentials;
    if (!parsed?.accessToken || typeof parsed.accessToken !== "string") return null;
    return parsed;
  } catch {
    // Absent or unreadable is simply "not logged in".
    return null;
  }
}

export async function writeCredentials(creds: StoredCredentials): Promise<void> {
  const dir = carrierHome();
  await mkdir(dir, { recursive: true, mode: 0o700 });
  // mkdir's mode is ignored when the directory already exists, so set it explicitly.
  await chmod(dir, 0o700).catch(() => undefined);
  const file = credentialsPath();
  await writeFile(file, `${JSON.stringify(creds, null, 2)}\n`, { mode: 0o600 });
  await chmod(file, 0o600).catch(() => undefined);
}

export async function clearCredentials(): Promise<void> {
  await rm(credentialsPath(), { force: true });
}

/** True when the access token is missing or within `skewMs` of expiry. */
export function isExpired(creds: StoredCredentials, skewMs = 60_000): boolean {
  if (!creds.expiresAt) return false;
  return Date.now() >= creds.expiresAt - skewMs;
}

/**
 * Never let a token reach a terminal or a log. Used on any string that might
 * have been built from a credential before it is printed.
 */
export function redact(text: string): string {
  return text
    .replace(/\b(ak_|sk_|rt_)[A-Za-z0-9._-]{6,}/g, "$1***")
    .replace(/\bBearer\s+[A-Za-z0-9._-]{8,}/gi, "Bearer ***");
}
